import type { School, UpdateSchoolRequest, CreateSchoolRequest } from '@timetable/types';
import { generateUniqueSchoolId } from '@timetable/utils';
import { handleApiCall } from './errors';
import { getAccessToken } from './auth-token';

// Get environment variables
const SUPABASE_URL = (import.meta as any).env?.VITE_SUPABASE_URL as string;
const SUPABASE_ANON_KEY = (import.meta as any).env?.VITE_SUPABASE_ANON_KEY as string;

if (!SUPABASE_URL || !SUPABASE_ANON_KEY) {
  throw new Error('Missing Supabase environment variables');
}

/**
 * Helper function to check if a School ID exists in the database
 * Used by generateUniqueSchoolId for collision detection
 */
export async function checkSchoolIdExists(schoolId: string): Promise<boolean> {
  return handleApiCall(async () => {
    const response = await fetch(
      `${SUPABASE_URL}/rest/v1/schools?school_id=eq.${schoolId}&select=id`,
      {
        headers: {
          apikey: SUPABASE_ANON_KEY,
        },
      }
    );

    if (!response.ok) {
      throw new Error(`Failed to check School ID: ${response.statusText}`);
    }

    const data = await response.json();
    return data && data.length > 0;
  });
}

/**
 * Get school by school_id
 */
export async function getSchoolDirect(schoolId: string): Promise<School> {
  const accessToken = getAccessToken();
  if (!accessToken) throw new Error('Not authenticated');

  const response = await fetch(
    `${SUPABASE_URL}/rest/v1/schools?school_id=eq.${schoolId}&select=*`,
    {
      headers: {
        apikey: SUPABASE_ANON_KEY,
        Authorization: `Bearer ${accessToken}`,
        'Content-Type': 'application/json',
      },
    }
  );

  if (!response.ok) {
    throw new Error(`Failed to fetch school: ${response.statusText}`);
  }

  const data = await response.json();
  if (!data || data.length === 0) {
    throw new Error('School not found');
  }

  return data[0];
}

/**
 * Update school settings
 */
export async function updateSchoolDirect(
  schoolId: string,
  updates: UpdateSchoolRequest
): Promise<School> {
  const accessToken = getAccessToken();
  if (!accessToken) throw new Error('Not authenticated');

  const response = await fetch(
    `${SUPABASE_URL}/rest/v1/schools?school_id=eq.${schoolId}`,
    {
      method: 'PATCH',
      headers: {
        apikey: SUPABASE_ANON_KEY,
        Authorization: `Bearer ${accessToken}`,
        'Content-Type': 'application/json',
        Prefer: 'return=representation',
      },
      body: JSON.stringify({
        ...updates,
        updated_at: new Date().toISOString(),
      }),
    }
  );

  if (!response.ok) {
    throw new Error(`Failed to update school: ${response.statusText}`);
  }

  const data = await response.json();
  return data[0];
}

/**
 * Upload school logo to Supabase Storage
 * Note: This is a placeholder implementation. In production, you would:
 * 1. Create a 'school-assets' storage bucket in Supabase
 * 2. Configure appropriate RLS policies
 * 3. Use the Supabase Storage API to upload files
 * 
 * For now, this returns a mock URL since storage setup is beyond the scope.
 */
export async function uploadSchoolLogoDirect(
  schoolId: string,
  file: File
): Promise<string> {
  // Validate file type
  const validTypes = ['image/png', 'image/jpeg', 'image/svg+xml'];
  if (!validTypes.includes(file.type)) {
    throw new Error('Invalid file type. Only PNG, JPG, and SVG are supported.');
  }

  // Validate file size (2MB max)
  const maxSize = 2 * 1024 * 1024; // 2MB in bytes
  if (file.size > maxSize) {
    throw new Error('File size exceeds 2MB limit.');
  }

  // TODO: Implement actual Supabase Storage upload
  // For now, return a placeholder URL
  console.warn('Logo upload not fully implemented. Returning placeholder URL.');
  return `https://placeholder.com/logos/${schoolId}/${file.name}`;
}

/**
 * Create a new school (Super Admin only)
 * Generates a unique 8-character School ID automatically
 */
export async function createSchoolDirect(
  schoolData: CreateSchoolRequest
): Promise<School> {
  return handleApiCall(async () => {
    const accessToken = getAccessToken();
    if (!accessToken) throw new Error('Not authenticated');

    // Generate unique School ID
    const schoolId = await generateUniqueSchoolId(checkSchoolIdExists);

    // Insert school record
    const response = await fetch(`${SUPABASE_URL}/rest/v1/schools`, {
      method: 'POST',
      headers: {
        apikey: SUPABASE_ANON_KEY,
        Authorization: `Bearer ${accessToken}`,
        'Content-Type': 'application/json',
        Prefer: 'return=representation',
      },
      body: JSON.stringify({
        ...schoolData,
        school_id: schoolId,
      }),
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.message || 'Failed to create school');
    }

    const data = await response.json();
    return data[0];
  });
}

/**
 * List all schools (Super Admin only)
 * Returns schools ordered by name
 */
export async function listSchoolsDirect(): Promise<School[]> {
  return handleApiCall(async () => {
    const accessToken = getAccessToken();
    if (!accessToken) throw new Error('Not authenticated');

    const params = new URLSearchParams();
    params.append('select', '*');
    params.append('order', 'school_name.asc');

    const response = await fetch(`${SUPABASE_URL}/rest/v1/schools?${params}`, {
      headers: {
        apikey: SUPABASE_ANON_KEY,
        Authorization: `Bearer ${accessToken}`,
      },
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.message || 'Failed to fetch schools');
    }

    return await response.json();
  });
}

/**
 * Delete a school by school_id (Super Admin only)
 * Uses a safe RPC that nulls audit_log references first to avoid FK violations
 * from audit triggers firing during cascade delete
 */
export async function deleteSchoolDirect(schoolId: string): Promise<void> {
  return handleApiCall(async () => {
    const accessToken = getAccessToken();
    if (!accessToken) throw new Error('Not authenticated');

    const response = await fetch(`${SUPABASE_URL}/rest/v1/rpc/delete_school_safe`, {
      method: 'POST',
      headers: {
        apikey: SUPABASE_ANON_KEY,
        Authorization: `Bearer ${accessToken}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ p_school_id: schoolId }),
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.message || 'Failed to delete school');
    }
  });
}
